import React, { useState, useEffect } from 'react';
import { getBlogPostSummaries } from '../../services/apiService';
import type { BlogPostSummary } from '../../services/apiService';
import { Loader } from 'react-feather';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../ui/LocalizedLink';

interface RelatedPostsProps {
  currentPostSlug: string;
  /** Called once the list has loaded (or failed), so the page can tell the prerenderer it is complete. */
  onLoaded?: () => void;
}

const RelatedPosts: React.FC<RelatedPostsProps> = ({ currentPostSlug, onLoaded }) => {
  const { t, i18n } = useTranslation('common');
  const [relatedPosts, setRelatedPosts] = useState<BlogPostSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    // The three articles that follow this one in date order (wrapping), so every article is linked from others.
    getBlogPostSummaries({ language: i18n.language, limit: 3, after: currentPostSlug.replace(/\/$/, '') })
      .then(posts => setRelatedPosts(posts.filter(post => post.slug !== currentPostSlug).slice(0, 3)))
      .catch(err => {
        console.error("Failed to load related posts:", err);
      })
      .finally(() => {
        setIsLoading(false);
        onLoaded?.();
      });
  }, [currentPostSlug, i18n.language]);

  if (isLoading) {
    return (
      <div className="py-16 text-center">
        <Loader className="animate-spin mx-auto text-brand-gold" size={32} />
      </div>
    );
  }

  if (relatedPosts.length === 0) {
    return null;
  }

  return (
    <div className="py-24 bg-white/5">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-12">{t('blogPostPage.readMore')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {relatedPosts.map(post => (
            <LocalizedLink 
              key={post.slug} 
              to={`/blog/${post.slug.replace(/\/$/, '')}`} 
              className="group block bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="aspect-video overflow-hidden">
                <img 
                  src={post.imageUrl} 
                  alt={post.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  width="400"
                  height="225"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-lg line-clamp-2">{post.title}</h3>
                <p className="text-sm text-white/70 mt-2 line-clamp-3">{post.summary}</p>
              </div>
            </LocalizedLink>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RelatedPosts;